import { useState, useEffect, axios, Button, Input, Card, CardContent, CardHeader, CardTitle, Label, Badge, Loader2, Plus, Copy, Trash2, Edit, Check, X, BACKEND_URL, API, useAuth, toast } from "@/pages/shared";
import { AFFILIATE_PROGRAMS, DISCLOSURE_RULES, PLATFORM_RULES } from "@/data/affiliateStarterKit";
import { ChevronDown, BookOpen, ShieldCheck, ExternalLink, Gift, Twitter, Linkedin, MessageCircle, Mail, BarChart3 } from "lucide-react";

// Auto-UTM builder — clicks on these share URLs are decoded by the backend
// redirect endpoint and forwarded onto the destination so the creator's own
// analytics (and our /attribution endpoint) can break clicks down by platform.
const buildShareUrl = (shortCode, platform) => {
  const base = `${BACKEND_URL}/api/r/${shortCode}`;
  const params = new URLSearchParams({
    utm_source: platform,
    utm_medium: "creatara_share",
    utm_content: shortCode,
  });
  return `${base}?${params.toString()}`;
};

export default function AffiliatesPage() {
  const { token } = useAuth();
  const [name, setName] = useState(""); const [url, setUrl] = useState(""); const [campaign, setCampaign] = useState(""); const [customSlug, setCustomSlug] = useState("");
  const [creating, setCreating] = useState(false); const [links, setLinks] = useState([]); const [loading, setLoading] = useState(true);
  const [editingSlug, setEditingSlug] = useState(null); const [newSlug, setNewSlug] = useState("");

  useEffect(() => { fetchLinks(); }, [token]);
  const fetchLinks = async () => { try { const res = await axios.get(`${API}/affiliates`, { headers: { Authorization: `Bearer ${token}` } }); setLinks(res.data); } catch (e) {} setLoading(false); };

  const createLink = async () => {
    if (!name.trim() || !url.trim()) { toast.error("Please fill required fields"); return; }
    setCreating(true);
    try { await axios.post(`${API}/affiliates`, { name, original_url: url, campaign, custom_slug: customSlug || null }, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Link created!"); setName(""); setUrl(""); setCampaign(""); setCustomSlug(""); fetchLinks(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setCreating(false);
  };

  const updateSlug = async (linkId) => {
    if (!newSlug.trim()) return;
    try { await axios.put(`${API}/affiliates/${linkId}/slug?new_slug=${newSlug}`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Slug updated!"); setEditingSlug(null); fetchLinks(); }
    catch (e) { toast.error(e.response?.data?.detail || "Slug taken"); }
  };

  const deleteLink = async (id) => { try { await axios.delete(`${API}/affiliates/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Deleted"); fetchLinks(); } catch (e) {} };
  const copyLink = (shortCode) => { navigator.clipboard.writeText(`${BACKEND_URL}/api/r/${shortCode}`); toast.success("Plain link copied"); };
  const copyLinkWithUtm = (shortCode, platform) => {
    navigator.clipboard.writeText(buildShareUrl(shortCode, platform));
    toast.success(`Tracked link for ${platform} copied`);
  };

  const shareOnPlatform = (link, platform) => {
    const trackedUrl = buildShareUrl(link.short_code, platform);
    const message = `Check this out: ${link.name}`;
    if (platform === "twitter") {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message + " " + trackedUrl)}`, "_blank");
    } else if (platform === "linkedin") {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(trackedUrl)}`, "_blank");
    } else if (platform === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(message + " " + trackedUrl)}`, "_blank");
    } else if (platform === "email") {
      const subject = encodeURIComponent(link.name);
      const body = encodeURIComponent(`${message}\n\n${trackedUrl}\n`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  const [attributionFor, setAttributionFor] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [attribLoading, setAttribLoading] = useState(false);
  const loadAttribution = async (linkId) => {
    if (attributionFor === linkId) { setAttributionFor(null); setAttribution(null); return; }
    setAttributionFor(linkId);
    setAttribLoading(true);
    try {
      const res = await axios.get(`${API}/affiliates/${linkId}/attribution`, { headers: { Authorization: `Bearer ${token}` } });
      setAttribution(res.data);
    } catch (e) { toast.error("Couldn't load attribution"); }
    setAttribLoading(false);
  };

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold mb-2">Affiliate Links</h1></div>

      {/* Starter Kit — free-tier PLG magnet */}
      <StarterKit />

      <Card className="glass">
        <CardHeader><CardTitle>Create New Link</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div><Label>Link Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Product Promo" className="mt-1 bg-white/5 border-white/10" /></div>
            <div className="lg:col-span-2"><Label>Destination URL</Label><Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" className="mt-1 bg-white/5 border-white/10" /></div>
            <div><Label>Custom Slug</Label><Input value={customSlug} onChange={(e) => setCustomSlug(e.target.value)} placeholder="my-product" className="mt-1 bg-white/5 border-white/10" /></div>
            <div><Label>Campaign</Label><Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="Summer Sale" className="mt-1 bg-white/5 border-white/10" /></div>
          </div>
          <Button onClick={createLink} disabled={creating} className="mt-4 btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full">{creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Create Link</Button>
        </CardContent>
      </Card>
      <Card className="glass">
        <CardHeader><CardTitle>Your Links</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : links.length === 0 ? <p className="text-muted-foreground text-center py-8">No links yet</p> : (
            <div className="space-y-4">
              {links.map((link) => (
                <div key={link.id} className="p-4 rounded-lg bg-white/5 space-y-3" data-testid={`affiliate-link-${link.short_code}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1"><p className="font-medium">{link.name}</p>{link.campaign && <Badge variant="secondary">{link.campaign}</Badge>}</div>
                      <p className="text-sm text-muted-foreground truncate">{link.original_url}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {editingSlug === link.id ? (
                          <div className="flex items-center gap-2">
                            <Input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} className="h-8 w-32 bg-white/5 border-white/10 text-sm" />
                            <Button size="sm" onClick={() => updateSlug(link.id)} className="h-8"><Check className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingSlug(null)} className="h-8"><X className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <><Badge variant="outline" className="text-xs">/r/{link.short_code}</Badge><Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => { setEditingSlug(link.id); setNewSlug(link.short_code); }}><Edit className="w-3 h-3" /></Button></>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="text-right"><p className="text-2xl font-bold text-teal-500">{link.clicks}</p><p className="text-xs text-muted-foreground">clicks</p></div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" title="Copy plain link" onClick={() => copyLink(link.short_code)} data-testid={`copy-plain-${link.short_code}`}><Copy className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" title="View per-platform clicks" onClick={() => loadAttribution(link.id)} data-testid={`attribution-${link.short_code}`}><BarChart3 className="w-4 h-4 text-cyan-400" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteLink(link.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </div>
                  </div>

                  {/* Auto-UTM share row — every share button below tags clicks back to the platform */}
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                    <span className="text-xs text-muted-foreground mr-1">Share with auto-tracking:</span>
                    <Button size="sm" variant="outline" className="h-7 text-blue-400 border-blue-400/30 hover:bg-blue-400/10" onClick={() => shareOnPlatform(link, "twitter")} data-testid={`share-twitter-${link.short_code}`}>
                      <Twitter className="w-3 h-3 mr-1" />X
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-blue-600 border-blue-600/30 hover:bg-blue-600/10" onClick={() => shareOnPlatform(link, "linkedin")} data-testid={`share-linkedin-${link.short_code}`}>
                      <Linkedin className="w-3 h-3 mr-1" />LinkedIn
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-green-500 border-green-500/30 hover:bg-green-500/10" onClick={() => shareOnPlatform(link, "whatsapp")} data-testid={`share-whatsapp-${link.short_code}`}>
                      <MessageCircle className="w-3 h-3 mr-1" />WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-amber-500 border-amber-500/30 hover:bg-amber-500/10" onClick={() => shareOnPlatform(link, "email")} data-testid={`share-email-${link.short_code}`}>
                      <Mail className="w-3 h-3 mr-1" />Email
                    </Button>
                    <div className="border-l border-white/10 h-5 mx-1" />
                    <span className="text-xs text-muted-foreground">Copy tracked URL:</span>
                    {["twitter", "linkedin", "instagram", "tiktok", "youtube"].map((p) => (
                      <Button key={p} size="sm" variant="ghost" className="h-7 text-xs px-2 capitalize" onClick={() => copyLinkWithUtm(link.short_code, p)} data-testid={`copy-utm-${p}-${link.short_code}`}>
                        {p}
                      </Button>
                    ))}
                  </div>

                  {/* Attribution drawer */}
                  {attributionFor === link.id && (
                    <div className="rounded-lg bg-black/30 border border-cyan-500/20 p-3" data-testid={`attribution-panel-${link.short_code}`}>
                      <p className="text-xs text-cyan-300 font-medium mb-2 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Per-platform click attribution</p>
                      {attribLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                      ) : attribution && attribution.by_source.length > 0 ? (
                        <div className="space-y-1.5">
                          {attribution.by_source.map((row) => {
                            const pct = attribution.total_clicks ? Math.round((row.clicks / attribution.total_clicks) * 100) : 0;
                            return (
                              <div key={row.source} className="flex items-center gap-3 text-sm">
                                <span className="w-24 capitalize text-muted-foreground">{row.source === "direct" ? "Direct / Unknown" : row.source}</span>
                                <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-cyan-400" style={{ width: `${pct}%` }} /></div>
                                <span className="w-16 text-right font-medium text-cyan-300">{row.clicks} · {pct}%</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No clicks yet — share the link above to start gathering attribution data.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ====================== AFFILIATE STARTER KIT ======================
const StarterKit = () => {
  const [open, setOpen] = useState(null); // "programs" | "disclosure" | null
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...Array.from(new Set(AFFILIATE_PROGRAMS.map((p) => p.category)))];
  const filtered = filter === "All" ? AFFILIATE_PROGRAMS : AFFILIATE_PROGRAMS.filter((p) => p.category === filter);

  return (
    <Card className="glass border-violet-500/20" data-testid="affiliate-starter-kit">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-600/10 flex items-center justify-center"><Gift className="w-5 h-5 text-violet-500" /></div>
          <div>
            <CardTitle className="flex items-center gap-2">Affiliate Content Starter Kit <Badge variant="secondary" className="text-xs">Free</Badge></CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Curated beginner programs and legal-safe disclosure rules — everything you need before your first promotion.</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 1. Top 25 Programs */}
        <button
          onClick={() => setOpen(open === "programs" ? null : "programs")}
          className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="starter-kit-programs-toggle"
        >
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-teal-500" />
            <div className="text-left">
              <p className="font-medium">Top 25 Beginner-Friendly Affiliate Programs</p>
              <p className="text-xs text-muted-foreground">Commission rate · Approval difficulty · Niche fit · Cookie window</p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 transition-transform ${open === "programs" ? "rotate-180" : ""}`} />
        </button>

        {open === "programs" && (
          <div className="rounded-lg bg-black/20 p-4 space-y-3" data-testid="starter-kit-programs-body">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`text-xs px-3 py-1 rounded-full transition ${filter === c ? "bg-violet-600 text-white" : "bg-white/5 hover:bg-white/10 text-muted-foreground"}`}
                  data-testid={`starter-kit-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >{c}</button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 pr-3">Program</th>
                    <th className="text-left py-2 pr-3">Commission</th>
                    <th className="text-left py-2 pr-3">Approval</th>
                    <th className="text-left py-2 pr-3">Niche fit</th>
                    <th className="text-left py-2 pr-3">Cookie</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.name} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{p.name}</div>
                        <Badge variant="outline" className="text-[10px] mt-1">{p.category}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-teal-400 font-medium whitespace-nowrap">{p.commission}</td>
                      <td className="py-2 pr-3">
                        <Badge
                          className={`text-[10px] ${
                            p.approval === "Easy" ? "bg-teal-600/20 text-teal-400" :
                            p.approval === "Medium" ? "bg-amber-600/20 text-amber-400" :
                            "bg-violet-600/20 text-violet-400"
                          }`}
                        >{p.approval}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground max-w-[260px]">{p.niche}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{p.cookie}</td>
                      <td className="py-2">
                        <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline inline-flex items-center gap-1 text-xs">
                          Apply <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground pt-2">Commission rates, cookie windows, and approval criteria are program-set and change periodically — always check the program's terms before launching a campaign.</p>
          </div>
        )}

        {/* 2. Disclosure Cheat Sheet */}
        <button
          onClick={() => setOpen(open === "disclosure" ? null : "disclosure")}
          className="w-full flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          data-testid="starter-kit-disclosure-toggle"
        >
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-500" />
            <div className="text-left">
              <p className="font-medium">Affiliate Disclosure Cheat Sheet</p>
              <p className="text-xs text-muted-foreground">FTC · ASA · EU DSA · platform-specific rules (Instagram, TikTok, YouTube, X, LinkedIn, blog, email)</p>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 transition-transform ${open === "disclosure" ? "rotate-180" : ""}`} />
        </button>

        {open === "disclosure" && (
          <div className="rounded-lg bg-black/20 p-4 space-y-5" data-testid="starter-kit-disclosure-body">
            {/* Regional regulators */}
            <div className="space-y-4">
              {DISCLOSURE_RULES.map((r) => (
                <div key={r.region} className="border-l-2 border-violet-500/40 pl-4">
                  <p className="font-semibold text-sm mb-2">{r.region}</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground mb-2">
                    {r.must_do.map((m, i) => (
                      <li key={i} className="flex items-start gap-2"><Check className="w-3 h-3 text-teal-500 mt-1 flex-shrink-0" /><span>{m}</span></li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-400/80">Penalty: {r.penalty}</p>
                  <a href={r.source} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-400 hover:underline inline-flex items-center gap-1 mt-1">
                    Official guidance <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>

            {/* Platform-specific */}
            <div className="pt-3 border-t border-white/10">
              <p className="font-semibold text-sm mb-3">Platform-specific rules</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PLATFORM_RULES.map((p) => (
                  <div key={p.platform} className="p-3 rounded-lg bg-white/5">
                    <p className="text-sm font-medium mb-1">{p.platform}</p>
                    <p className="text-xs text-muted-foreground">{p.rule}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xs text-muted-foreground pt-2 italic">This cheat sheet is general guidance, not legal advice. Rules update — check regulator sites and consult a lawyer for material commercial activity.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
